import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Alert, AlertTitle, Box, Button, Collapse, IconButton, Tooltip } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import StopIcon from '@mui/icons-material/Stop';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import HistoryIcon from '@mui/icons-material/History';
import { useTranslation } from 'react-i18next';
import { INotification, NotificationSeverity } from '../../models/INotification';
import NotificationServiceInstance from '../../services/NotificationService';
import * as signalR from "@microsoft/signalr";
import { signalRTimingHubUrl } from '../../services/ServiceConstants';
import DateHelperInstance from '../../services/DateHelper';

function getSeverityColor(severity: NotificationSeverity): 'info' | 'warning' | 'error' {
    switch (severity) {
        case NotificationSeverity.Information:
            return 'info';
        case NotificationSeverity.Important:
            return 'warning';
        case NotificationSeverity.Danger:
            return 'error';
        default:
            return 'info';
    }
}

function getSeverityLabel(severity: NotificationSeverity, t: (key: string) => string): string {
    switch (severity) {
        case NotificationSeverity.Information:
            return t('notifications.severityInfo');
        case NotificationSeverity.Important:
            return t('notifications.severityImportant');
        case NotificationSeverity.Danger:
            return t('notifications.severityDanger');
        default:
            return '';
    }
}

interface NotificationsBannerProps {
    raceId: string;
    riderId?: string;
    canManage?: boolean;
    onFinish?: (notification: INotification) => void;
}

export default function NotificationsBanner(props: NotificationsBannerProps) {
    const { raceId, riderId, canManage, onFinish } = props;
    const { t } = useTranslation('common');
    const [notifications, setNotifications] = useState<INotification[]>([]);
    const [seenIds, setSeenIds] = useState<Set<string>>(new Set());
    const [showAll, setShowAll] = useState(false);
    const [showHistory, setShowHistory] = useState(false);

    const fetchNotifications = useCallback(async () => {
        try {
            const data = riderId
                ? await NotificationServiceInstance.getRiderNotifications(raceId, riderId)
                : await NotificationServiceInstance.getEventNotifications(raceId);
            setNotifications(data);
        } catch (e) {
            console.error('Failed to fetch notifications', e);
        }
    }, [raceId, riderId]);

    const hubConnectionRef = useRef<signalR.HubConnection>();

    useEffect(() => {
        // Load initial notifications via API
        fetchNotifications();

        // Subscribe to real-time updates via SignalR
        const hubConnection = new signalR.HubConnectionBuilder()
            .withUrl(signalRTimingHubUrl)
            .withAutomaticReconnect()
            .configureLogging(signalR.LogLevel.Information)
            .build();

        hubConnectionRef.current = hubConnection;

        hubConnection.start().then(() => {
            if (hubConnection.connectionId) {
                hubConnection.invoke("JoinRace", raceId);
            }
        }).catch(e => console.error('Failed to start notification SignalR connection', e));

        // Re-join the race group after automatic reconnection
        hubConnection.onreconnected(() => {
            hubConnection.invoke("JoinRace", raceId);
        });

        // Listen for event notifications
        hubConnection.on("notification", (notification: INotification) => {
            setNotifications(prev => {
                const exists = prev.find(n => n.notificationId === notification.notificationId);
                if (exists) {
                    return prev.map(n => n.notificationId === notification.notificationId ? notification : n);
                }
                return [...prev, notification];
            });
        });

        // Listen for rider notifications
        hubConnection.on("rider-notification", (notification: INotification) => {
            if (riderId && notification.riderId === riderId) {
                setNotifications(prev => {
                    const exists = prev.find(n => n.notificationId === notification.notificationId);
                    if (exists) {
                        return prev.map(n => n.notificationId === notification.notificationId ? notification : n);
                    }
                    return [...prev, notification];
                });
            }
        });

        return () => {
            hubConnection.stop().then(() => console.log("notification connection closed"));
        };
    }, [fetchNotifications, raceId, riderId]);

    const handleDismiss = (id: string) => {
        setSeenIds(prev => new Set(prev).add(id));
    };

    const now = new Date();

    const isNotificationActive = (n: INotification): boolean => {
        if (!n.isActive) return false;
        const start = new Date(n.notificationStart);
        if (!DateHelperInstance.isValidDate(start)) return false;
        if (n.notificationEnd) {
            const end = new Date(n.notificationEnd);
            if (!DateHelperInstance.isValidDate(end)) return false;
            return start <= now && end >= now;
        }
        return start <= now;
    };

    const activeNotifications = notifications.filter(n => isNotificationActive(n));

    // All notifications sorted by severity descending (Danger first)
    const allSorted = [...notifications].sort((a, b) => b.severity - a.severity);

    // Sort by severity descending (Danger first)
    const sortedNotifications = [...activeNotifications].sort((a, b) => b.severity - a.severity);

    const visibleNotifications = showHistory
        ? allSorted
        : showAll
            ? sortedNotifications
            : sortedNotifications.filter(n =>
                !seenIds.has(n.notificationId) || n.severity === NotificationSeverity.Danger
            );

    const hasInactiveNotifications = notifications.length > activeNotifications.length;

    if (visibleNotifications.length === 0 && !showAll && !showHistory) {
        if (activeNotifications.length > 0 || hasInactiveNotifications) {
            return (
                <Box sx={{ mb: 1, display: 'flex', gap: 1 }}>
                    {activeNotifications.length > 0 && (
                        <Button
                            size="small"
                            startIcon={<ExpandMoreIcon />}
                            onClick={() => setShowAll(true)}
                        >
                            {t('notifications.showAll')} ({activeNotifications.length})
                        </Button>
                    )}
                    {hasInactiveNotifications && (
                        <Button
                            size="small"
                            startIcon={<HistoryIcon />}
                            onClick={() => setShowHistory(true)}
                        >
                            {t('notifications.showHistory')} ({notifications.length})
                        </Button>
                    )}
                </Box>
            );
        }
        return null;
    }

    return (
        <Box sx={{ mb: 2 }}>
            {visibleNotifications.map(n => {
                const active = isNotificationActive(n);
                return (
                    <Collapse key={n.notificationId} in={true}>
                        <Alert
                            severity={getSeverityColor(n.severity)}
                            variant={active ? 'standard' : 'outlined'}
                            sx={{ mb: 1, opacity: active ? 1 : 0.7 }}
                            action={
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    {canManage && onFinish && active && (
                                        <Tooltip title={t('notifications.finishNotification')}>
                                            <IconButton
                                                aria-label={t('notifications.finishNotification') as string}
                                                color="inherit"
                                                size="small"
                                                onClick={() => onFinish(n)}
                                            >
                                                <StopIcon fontSize="inherit" />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                    {active && !seenIds.has(n.notificationId) && (
                                        <IconButton
                                            aria-label={t('notifications.dismiss') as string}
                                            color="inherit"
                                            size="small"
                                            onClick={() => handleDismiss(n.notificationId)}
                                        >
                                            <CloseIcon fontSize="inherit" />
                                        </IconButton>
                                    )}
                                </Box>
                            }
                        >
                            <AlertTitle>{getSeverityLabel(n.severity, t)}</AlertTitle>
                            {n.description}
                        </Alert>
                    </Collapse>
                );
            })}
            <Box sx={{ display: 'flex', gap: 1 }}>
                {showHistory ? (
                    <Button
                        size="small"
                        startIcon={<ExpandLessIcon />}
                        onClick={() => setShowHistory(false)}
                    >
                        {t('notifications.hideHistory')}
                    </Button>
                ) : (
                    <>
                        {showAll ? (
                            <Button
                                size="small"
                                startIcon={<ExpandLessIcon />}
                                onClick={() => setShowAll(false)}
                            >
                                {t('notifications.showUnseen')}
                            </Button>
                        ) : (
                            activeNotifications.length > visibleNotifications.length && (
                                <Button
                                    size="small"
                                    startIcon={<ExpandMoreIcon />}
                                    onClick={() => setShowAll(true)}
                                >
                                    {t('notifications.showAll')} ({activeNotifications.length})
                                </Button>
                            )
                        )}
                        {hasInactiveNotifications && (
                            <Button
                                size="small"
                                startIcon={<HistoryIcon />}
                                onClick={() => setShowHistory(true)}
                            >
                                {t('notifications.showHistory')} ({notifications.length})
                            </Button>
                        )}
                    </>
                )}
            </Box>
        </Box>
    );
}
